// Client-side auth identity / token storage (phase 11.7 + 11.8).
//
// Solo user UX goal: zero login screen. The app silently registers a
// per-device anonymous account on first launch and silently resumes on every
// subsequent launch. To make resume work across app restarts and debug-browser
// reloads, we persist:
//   - `bbgm.auth.deviceId`  — stable across resets; used to recover an
//     account if the token is gone (e.g. localStorage cleared on this
//     device's first load, or after authFail("invalid")).
//   - `bbgm.auth.token`     — 256-bit server-issued session secret.
//     Lives in localStorage, not a cookie: WebSocket auth happens after
//     `open`, not as part of HTTP upgrade, so cookies wouldn't help.
//
// On Capacitor (iOS/Android), the WebView's localStorage is durable across app
// restarts and quits. It's wiped on uninstall and isn't shared across devices.
// Phase 13 will migrate the token to Keychain / Android Keystore (encrypted at
// rest, optionally synced via Keychain shared groups) and add email binding for
// cross-device recovery.
//
// Security caveat: localStorage is plaintext. A jailbroken phone or
// malicious extension in the debug harness can lift the token and impersonate
// the user until they sign out. The doc accepts this for phase 11; phase
// 13's secure-storage migration closes the gap.

const DEVICE_ID_KEY = "bbgm.auth.deviceId";
const TOKEN_KEY = "bbgm.auth.token";

const safeGet = (key: string): string | null => {
	try {
		return window.localStorage.getItem(key);
	} catch {
		// Storage unavailable (private mode, sandboxed iframe, OS-blocked).
		// Returning null is the same as "no stored value" — callers degrade
		// gracefully (e.g. a fresh deviceId, or device-path signup).
		return null;
	}
};

const safeSet = (key: string, value: string): void => {
	try {
		window.localStorage.setItem(key, value);
	} catch {
		// If we can't persist, fall through silently. The session is still
		// usable in memory for the current page lifetime — only the resume
		// path on next load is lost.
	}
};

const safeRemove = (key: string): void => {
	try {
		window.localStorage.removeItem(key);
	} catch {
		// As above — non-fatal.
	}
};

/**
 * Stable device identifier. Created on first call, reused forever.
 * Fallback to a per-page-load value if storage is unavailable; the
 * server will treat that as a brand new device each time, which is
 * acceptable degraded behavior but not great UX. See safeGet docstring.
 */
export const getOrCreateDeviceId = (): string => {
	const existing = safeGet(DEVICE_ID_KEY);
	if (existing) return existing;
	const fresh = crypto.randomUUID();
	safeSet(DEVICE_ID_KEY, fresh);
	return fresh;
};

/** Currently-stored session token, or null if we have none. */
export const loadStoredToken = (): string | null => safeGet(TOKEN_KEY);

/** Persist a token from the server's authOk reply. */
export const saveToken = (token: string): void => {
	safeSet(TOKEN_KEY, token);
};

/**
 * Drop the stored token. Called on authFail("expired"|"invalid") so the
 * reconnect path falls back to the device credential, and on explicit
 * sign-out (phase 12+ when we add that UI).
 */
export const clearToken = (): void => {
	safeRemove(TOKEN_KEY);
};
